import { useEffect, useState } from "react";
import { AlertCircle, Loader2, PenLine } from "lucide-react";
import { toast } from "sonner";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogFooter,
  appDialogPrimaryButton,
  appDialogSecondaryButton,
} from "@/components/ui/app-dialog";
import {
  createEndorsement,
  getEndorsementPrefill,
  uploadEndorsementFile,
} from "@/Api/endorsements";
import { EndorsementPrefill } from "@/types/endorsement";
import { Mask } from "@/utils/mask";
import { isValidCnpj, isValidCpf } from "@/utils/pixKey";
import { FilePicker } from "./FilePicker";

type RequestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  saleId: number | null;
  onSuccess?: () => void;
};

const onlyDigits = (value: string | null | undefined) => (value ?? "").replace(/\D/g, "");

const documentErrorFor = (digits: string): string | null => {
  if (digits === "") return "CPF não cadastrado. Preencha para solicitar o endossamento.";
  if (digits.length === 11) return isValidCpf(digits) ? null : "CPF inválido.";
  if (digits.length === 14) return isValidCnpj(digits) ? null : "CNPJ inválido.";
  return "CPF inválido.";
};

export const EndorsementRequestModal = ({ isOpen, onClose, saleId, onSuccess }: RequestModalProps) => {
  const [prefill, setPrefill] = useState<EndorsementPrefill | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [document, setDocument] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [confirmedChange, setConfirmedChange] = useState(false);

  useEffect(() => {
    if (!isOpen || !saleId) return;

    setLoading(true);
    getEndorsementPrefill(saleId)
      .then(({ ok, data }) => {
        if (!ok) {
          onClose();
          return;
        }
        const response: EndorsementPrefill = data.response;
        setPrefill(response);
        setName(response.client_name ?? "");
        setDocument(Mask.maskCpfOrCnpj(response.client_document ?? ""));
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, saleId]);

  const reset = () => {
    setPrefill(null);
    setName("");
    setEditingName(false);
    setDocument("");
    setDescription("");
    setFiles([]);
    setConfirmedChange(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const documentDigits = onlyDigits(document);
  const documentError = documentErrorFor(documentDigits);
  const nameError = name.trim().length < 3 ? "Informe o nome completo do cliente." : null;
  const dataChanged =
    !!prefill &&
    (name.trim() !== (prefill.client_name ?? "").trim() ||
      documentDigits !== onlyDigits(prefill.client_document));
  const blocked = !!prefill?.has_active_request;
  // LOT-68: foto ou vídeo é obrigatório para solicitar
  const missingFile = files.length === 0;
  const canSubmit =
    !!prefill &&
    !blocked &&
    !documentError &&
    !nameError &&
    (!dataChanged || confirmedChange) &&
    !missingFile &&
    !submitting;

  const handleSubmit = async () => {
    if (!saleId || !canSubmit) return;

    setSubmitting(true);
    try {
      const [firstFile, ...otherFiles] = files;
      const { ok, data } = await createEndorsement({
        sale_id: saleId,
        client_name: name.trim(),
        client_document: documentDigits,
        description: description.trim() || undefined,
        file: firstFile,
      });
      if (!ok) return;

      const created = data.response;
      let failed = 0;
      for (const file of otherFiles) {
        const upload = await uploadEndorsementFile(created.uuid, file);
        if (!upload.ok) failed++;
      }

      if (failed > 0) {
        toast.warning(
          `Endossamento solicitado, mas ${failed} anexo(s) não foram enviados. Reenvie pela aba Endossamento.`
        );
      } else {
        toast.success("Endossamento solicitado com sucesso!");
      }

      reset();
      onClose();
      onSuccess?.();
    } finally {
      setSubmitting(false);
    }
  };

  const alertBox = (tone: "red" | "amber", text: string) => (
    <div
      role="alert"
      className={`flex items-start gap-1.5 rounded border px-2 py-1.5 text-xs font-medium ${
        tone === "red"
          ? "border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
          : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
      }`}
    >
      <AlertCircle
        className={`mt-0.5 h-4 w-4 shrink-0 ${tone === "red" ? "text-red-600" : "text-amber-600"}`}
      />
      <p>{text}</p>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <AppDialogContent
        icon={PenLine}
        title="Solicitar Endossamento"
        description="Confirme os dados do cliente e anexe foto ou vídeo."
      >
        {loading || !prefill ? (
          <div className="flex justify-center px-6 py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <AppDialogBody>
              {blocked &&
                alertBox(
                  "red",
                  "Esta venda já possui uma solicitação de endossamento ativa. Uma nova solicitação só é possível após recusa."
                )}

              <div className="space-y-2">
                <Label>Nome completo</Label>
                {editingName ? (
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value.toUpperCase())}
                    aria-invalid={!!nameError}
                  />
                ) : (
                  <div className="flex items-center justify-between rounded-md border bg-muted p-2">
                    <span className="text-sm">{name || "—"}</span>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setEditingName(true)}
                      className="h-6 bg-primary px-2 text-xs text-primary-foreground hover:bg-primary/90"
                    >
                      Alterar
                    </Button>
                  </div>
                )}
                {editingName && nameError && <p className="text-sm text-red-500">{nameError}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="endorsement_document">CPF do cliente</Label>
                {documentError && alertBox("red", documentError)}
                <Input
                  id="endorsement_document"
                  value={document}
                  onChange={(e) => setDocument(Mask.maskCpfOrCnpj(e.target.value))}
                  inputMode="numeric"
                  placeholder="000.000.000-00"
                  aria-invalid={!!documentError}
                  className={documentError ? "border-red-500 text-red-700 focus-visible:ring-red-500" : ""}
                />
                {alertBox("amber", "ALERTA: Você confirma que o CPF é o do mesmo cliente que comprou a aposta!")}
              </div>

              {dataChanged && (
                <div className="space-y-2 rounded border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/40">
                  <p className="text-xs font-medium text-amber-900 dark:text-amber-200">
                    Você alterou o nome ou o CPF. Essa mudança também atualizará o cadastro do cliente.
                  </p>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="endorsement-confirm-change"
                      checked={confirmedChange}
                      onCheckedChange={(checked) => setConfirmedChange(checked === true)}
                    />
                    <Label
                      htmlFor="endorsement-confirm-change"
                      className="cursor-pointer text-xs font-medium text-amber-900 dark:text-amber-200"
                    >
                      Tenho certeza e quero atualizar o cadastro
                    </Label>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="endorsement_description">Descrição</Label>
                <Textarea
                  id="endorsement_description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Observações sobre o endossamento (opcional)..."
                  className="min-h-[80px]"
                />
              </div>

              <div className="space-y-2">
                <Label>Foto / vídeo *</Label>
                <FilePicker files={files} onChange={setFiles} disabled={submitting || blocked} />
                {missingFile && !blocked && (
                  <p className="text-xs font-medium text-red-600">
                    Anexe ao menos uma foto ou um vídeo para solicitar o endossamento.
                  </p>
                )}
              </div>
            </AppDialogBody>

            <AppDialogFooter>
              <Button
                type="button"
                className={appDialogSecondaryButton}
                onClick={handleClose}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                className={appDialogPrimaryButton}
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {submitting ? "Enviando..." : "Solicitar Endosso"}
              </Button>
            </AppDialogFooter>
          </>
        )}
      </AppDialogContent>
    </Dialog>
  );
};
